import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useState } from 'react';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import type { DropCardInput } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Dialog } from '@sorare/core/src/components/dialog';
import { sportProfile } from '@sorare/core/src/contexts/currentUser/queries';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import type { ColorFormat } from '@sorare/core/src/lib/colors';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { TinderCardSwipe } from '../TinderCardSwipe';
import { PackTear } from './PackTear';
import type {
  RevealPackCardsDialog_anyCardInterface,
  claimPackCardsOnCloseMutation,
  claimPackCardsOnCloseMutationVariables,
} from './__generated__/index.graphql';

const StyledDialog = styled(Dialog)`
  @media ${laptopAndAbove} {
    max-height: 90vh;
  }
`;

const Root = styled(Vertical)`
  width: 100%;
  position: relative;
  isolation: isolate;
  flex: 1;
  min-height: 100%;
  overflow: visible;
  background: linear-gradient(
      to bottom,
      transparent 0%,
      transparent 70%,
      var(--secondary-color) 100%
    ),
    linear-gradient(to bottom, transparent 0%, var(--secondary-color) 100%),
    linear-gradient(
      to bottom,
      var(--secondary-color) 0%,
      var(--primary-color) 100%
    );
`;
const TinderCardSwipeWrapper = styled(Vertical)`
  width: 100%;
  flex: 1;
  &.hidden {
    opacity: 0;
    position: absolute;
  }
`;

const CLAIM_PACK_CARDS_ON_CLOSE_MUTATION = gql`
  mutation claimPackCardsOnCloseMutation(
    $input: claimCardsFromPackInput!
    $sport: Sport!
  ) {
    claimCardsFromPack(input: $input) {
      currentUser {
        slug
        ...CurrentUseProvider_sportProfile
      }
      errors {
        message
        code
      }
    }
  }
  ${sportProfile}
` as TypedDocumentNode<
  claimPackCardsOnCloseMutation,
  claimPackCardsOnCloseMutationVariables
>;

type Props = {
  open: boolean;
  onClose?: () => void;
  cards: RevealPackCardsDialog_anyCardInterface[];
  onContinue: () => void;
  packId: string;
  packPictureUrl?: string;
  isFirstPack?: boolean;
  initialPackAnimationDone?: boolean;
  primaryColor: ColorFormat;
  secondaryColor: ColorFormat;
  firstStepTitle?: ReactNode;
  showPlayerDetails?: boolean;
};

export const RevealPackCardsDialog = ({
  open,
  onClose,
  cards,
  onContinue,
  isFirstPack,
  packId,
  packPictureUrl,
  initialPackAnimationDone = false,
  firstStepTitle,
  showPlayerDetails,
  primaryColor,
  secondaryColor,
}: Props) => {
  const [packAnimationDone, setPackAnimationDone] = useState(
    initialPackAnimationDone
  );
  const { sport } = useSportContext();

  const [displayRecap, setDisplayRecap] = useState(false);

  const [lastCardRevealed, setLastCardRevealed] = useState(false);
  const [claimPackCardsOnClose] = useMutation(
    CLAIM_PACK_CARDS_ON_CLOSE_MUTATION
  );

  const [droppedCards, setDroppedCards] = useState<DropCardInput[]>([]);
  const { showNotification } = useSnackNotificationContext();

  const onNextCard = (index: number) => {
    const isLastCard = cards.length - 1 === index;
    if (isLastCard) {
      setLastCardRevealed(true);
    }
  };

  const handleClose = async () => {
    onClose?.();
    if (!lastCardRevealed || displayRecap) {
      if (packId) {
        await claimPackCardsOnClose({
          variables: {
            sport,
            input: {
              packId,
              chosenCardSlugs: cards
                .map(card => card.slug)
                .filter(slug =>
                  droppedCards.every(
                    droppedCard => droppedCard.cardSlug !== slug
                  )
                ),
            },
          },
        });
        if (!displayRecap) {
          showNotification('packOpeningInterrupted');
        }
      }
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      hideHeader
      fullWidth
      maxWidth="xs"
      disableBackdropClick
    >
      {({ CloseButton }) => (
        <Root
          style={
            {
              '--primary-color': primaryColor,
              '--secondary-color': secondaryColor,
            } as CSSProperties
          }
        >
          <TinderCardSwipeWrapper
            className={classNames({ hidden: !packAnimationDone })}
          >
            <TinderCardSwipe
              cards={cards}
              onContinue={onContinue}
              isFirstPack={isFirstPack}
              packId={packId}
              droppedCards={droppedCards}
              setDroppedCards={setDroppedCards}
              packAnimationDone={packAnimationDone}
              closeButton={
                !!handleClose &&
                !isFirstPack && <CloseButton onClose={handleClose} />
              }
              showPlayerDetails={showPlayerDetails}
              displayRecap={displayRecap}
              setDisplayRecap={setDisplayRecap}
              onNextCard={onNextCard}
              overlayColor={primaryColor}
            />
          </TinderCardSwipeWrapper>
          {!packAnimationDone && (
            <PackTear
              onDone={() => {
                setPackAnimationDone(true);
              }}
              firstCardUrl={cards.at(0)?.pictureUrl || ''}
              loading={!cards.length || !packPictureUrl}
              packPictureUrl={packPictureUrl}
              closeButton={
                !!handleClose &&
                !isFirstPack && <CloseButton onClose={handleClose} />
              }
              title={firstStepTitle}
            />
          )}
        </Root>
      )}
    </StyledDialog>
  );
};

RevealPackCardsDialog.fragments = {
  anyCardInterface: gql`
    fragment RevealPackCardsDialog_anyCardInterface on AnyCardInterface {
      slug
      pictureUrl
      ...TinderCardSwipe_anyCardInterface
    }
    ${TinderCardSwipe.fragments.anyCardInterface}
  ` as TypedDocumentNode<RevealPackCardsDialog_anyCardInterface>,
};
